import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBuilding,
  faHandshake,
  faMagnifyingGlass,
  faMicrophone,
  faUser,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';

import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { InputGroup } from '@buildoutinc/blueprint-react/ui/InputGroup';
import { List } from '@buildoutinc/blueprint-react/ui/List';
import { Tab, Tabs, TabsList } from '@buildoutinc/blueprint-react/ui/Tabs';

import { Button } from 'components/ui/button';
import LoadingSpinner from 'components/ui/loading_spinner';

// Just a CustomEvent dispatcher — importing it does not pull the assistant bundle in here.
import { openAssistant } from 'components/ai/assistant/events';
import { speechToTextSupported, useSpeechToText } from 'hooks/use_speech_to_text';

import { debouncedSearchOmni, searchOmni } from './api';
import AskAiRow from './ask_ai_row';
import SparkSearchIcon from './spark_search_icon';
import type {
  OmniSearchNavTarget,
  OmniSearchResult,
  OmniSearchResults,
  OmniSearchSection,
  OmniSearchTab,
} from './types';

import './styles.scss';

const TABS: { label: string; value: OmniSearchTab }[] = [
  { label: 'All', value: 'all' },
  { label: 'Contacts', value: 'contacts' },
  { label: 'Properties', value: 'properties' },
  { label: 'Deals', value: 'deals' },
];

const SECTION_ORDER: OmniSearchSection[] = ['contacts', 'properties', 'deals'];

const SECTION_LABELS: Record<OmniSearchSection, string> = {
  contacts: 'Contacts',
  properties: 'Properties',
  deals: 'Deals',
};

const SECTION_TYPE_LABELS: Record<OmniSearchSection, string> = {
  contacts: 'Contact',
  properties: 'Property',
  deals: 'Deal',
};

const SECTION_ICONS: Record<OmniSearchSection, IconDefinition> = {
  contacts: faUser,
  properties: faBuilding,
  deals: faHandshake,
};

function shortcutLabel(): string {
  if (typeof navigator === 'undefined') return 'Ctrl K';

  return /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent) ? '⌘K' : 'Ctrl K';
}

function isTextEntry(element: HTMLElement | null): boolean {
  if (!element) return false;

  return element.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName);
}

interface Props {
  aiEnabled?: boolean;
  recordSearchEnabled?: boolean;
}

export default function OmniSearch({ aiEnabled = false, recordSearchEnabled = true }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const requestIdRef = useRef(0);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<OmniSearchTab>('all');
  const [results, setResults] = useState<OmniSearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const { cancel: cancelDictation, recording, toggle: toggleDictation } = useSpeechToText({
    text: query,
    onTranscript: setQuery,
    onFinish: setQuery,
  });

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        cancelDictation();
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        cancelDictation();
        inputRef.current?.blur();
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [cancelDictation, open]);

  useEffect(() => {
    function handleShortcut(event: KeyboardEvent) {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
      if (event.key.toLowerCase() !== 'k') return;

      const target = event.target as HTMLElement | null;
      if (isTextEntry(target) && !containerRef.current?.contains(target)) return;

      event.preventDefault();
      setOpen(true);
      inputRef.current?.focus();
    }

    document.addEventListener('keydown', handleShortcut);

    return () => document.removeEventListener('keydown', handleShortcut);
  }, []);

  useEffect(() => () => debouncedSearchOmni.cancel(), []);

  useEffect(() => {
    // AI owns the top row for now so if it is enabled set the index to 0
    setActiveIndex(aiEnabled ? 0 : -1);

    // Without record search there is nothing to fetch, so the endpoint is never called at all.
    if (!open || !recordSearchEnabled) {
      debouncedSearchOmni.cancel();
      requestIdRef.current += 1;
      setResults(null);
      setLoading(false);
      return;
    }

    const trimmed = query.trim();
    setLoading(true);
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    const handleResults = (nextResults: OmniSearchResults) => {
      if (requestId !== requestIdRef.current) return;
      setResults(nextResults);
      setLoading(false);
    };

    // A blank query asks the endpoint for recents; fetch those immediately since there is no typing
    // to debounce. Actual queries stay debounced so keystrokes coalesce into one request.
    if (trimmed) {
      debouncedSearchOmni({ query: trimmed, scope: tab }, handleResults);
    } else {
      debouncedSearchOmni.cancel();
      searchOmni({ query: '', scope: tab }, handleResults);
    }
  }, [aiEnabled, open, query, recordSearchEnabled, tab]);

  const hasQuery = query.trim().length > 0;

  const sections = SECTION_ORDER
    .map(section => [section, results?.[section] ?? []] as const)
    .filter(([, items]) => items.length > 0);

  const flatResults = sections.reduce<OmniSearchResult[]>((all, [, items]) => all.concat(items), []);

  // AI takes index 0 whenever it renders, which pushes the record rows down by one.
  // Records drop out of the list while a search is in flight because they are hidden behind
  // the spinner, but Ask AI stays available the whole time.
  const navTargets: OmniSearchNavTarget[] = [
    ...(aiEnabled ? [{ kind: 'askAi' } as const] : []),
    ...(loading ? [] : flatResults.map(result => ({ kind: 'result', result }) as const)),
  ];
  const resultIndexOffset = aiEnabled ? 1 : 0;

  // The placeholder should not promise a half of the bar the user's flags do not include.
  let placeholder = 'Search';
  if (aiEnabled) placeholder = recordSearchEnabled ? 'Search or ask Otto' : 'Ask Otto';

  function askAssistant() {
    const trimmed = query.trim();

    cancelDictation();
    openAssistant(trimmed ? { prompt: trimmed } : {});
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  }

  function handleClear(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    cancelDictation();

    if (query) {
      setQuery('');
      inputRef.current?.focus();
      return;
    }

    setOpen(false);
    inputRef.current?.blur();
  }

  function handleMicClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    setOpen(true);
    toggleDictation();
  }

  useEffect(() => {
    if (activeIndex < 0) return;

    const activeNode = containerRef.current?.querySelector('.omni-search-result--active');
    if (activeNode && typeof activeNode.scrollIntoView === 'function') {
      activeNode.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (navTargets.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(index => Math.min(index + 1, navTargets.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(index => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      let target: OmniSearchNavTarget | null = null;
      if (activeIndex >= 0) {
        target = navTargets[activeIndex] ?? null;
      } else if (hasQuery) {
        target = navTargets[0];
      }

      if (!target) return;

      event.preventDefault();
      if (target.kind === 'askAi') {
        askAssistant();
      } else {
        window.location.assign(target.result.url);
      }
    }
  }

  function renderContent() {
    if (loading) {
      return <LoadingSpinner loadingText={hasQuery ? 'Searching…' : 'Loading…'} size="sm" skipTopBuffer />;
    }

    if (sections.length === 0) {
      return (
        <Empty variant="inline">
          <Empty.Content>{hasQuery ? `No results for "${query.trim()}"` : 'No recent items yet.'}</Empty.Content>
        </Empty>
      );
    }

    return sections.map(([section, items], sectionIndex) => {
      const sectionOffset = sections
        .slice(0, sectionIndex)
        .reduce((total, [, sectionItems]) => total + sectionItems.length, 0);

      return (
        <div className="omni-search-section" key={section}>
          <p className="omni-search-section-title fw-semibold">
            {SECTION_LABELS[section]}
          </p>
          <List flush>
            {items.map((item, itemIndex) => {
              const isActive = resultIndexOffset + sectionOffset + itemIndex === activeIndex;

              return (
                <List.Item
                  asAction
                  className={classNames('align-items-center border-0', {
                    'omni-search-result--active': isActive,
                  })}
                  key={`${section}-${item.id}`}
                  render={<a href={item.url} />}
                >
                  <span
                    className={classNames(
                      'omni-search-result-icon d-flex align-items-center justify-content-center flex-shrink-0',
                      `omni-search-result-icon--${section}`,
                    )}
                  >
                    <FontAwesomeIcon icon={SECTION_ICONS[section]} />
                  </span>
                  <List.ItemContent>
                    <List.ItemTitle>{item.title}</List.ItemTitle>
                    {item.subtitle && <List.ItemDescription>{item.subtitle}</List.ItemDescription>}
                  </List.ItemContent>
                  <List.ItemActions>
                    <span className="omni-search-result-type fs-small">{SECTION_TYPE_LABELS[section]}</span>
                  </List.ItemActions>
                </List.Item>
              );
            })}
          </List>
        </div>
      );
    });
  }

  return (
    <div
      className={classNames('omni-search-container position-relative', {
        'omni-search-container--expanded': open,
      })}
      data-testid="omni-search"
      ref={containerRef}
      onClick={() => setOpen(true)}
    >
      <Card className="omni-search-panel">
        <InputGroup
          className={classNames('omni-search align-items-center flex-nowrap', {
            'omni-search--open': open,
          })}
        >
          <InputGroup.Addon>
            {aiEnabled
              ? <SparkSearchIcon />
              : <FontAwesomeIcon className="omni-search-icon" icon={faMagnifyingGlass} />}
          </InputGroup.Addon>
          <Input
            data-testid="omni-search-input"
            placeholder={recording ? 'Listening…' : placeholder}
            ref={inputRef}
            value={query}
            onFocus={() => setOpen(true)}
            onKeyDown={handleInputKeyDown}
            onValueChange={setQuery}
          />
          {!open && (
            <span className="omni-search-shortcut fs-xs flex-shrink-0" data-testid="omni-search-shortcut">
              {shortcutLabel()}
            </span>
          )}
          {speechToTextSupported() && (
            <InputGroup.Addon>
              <Button
                aria-label={recording ? 'Stop dictation' : 'Search by voice'}
                className={classNames('omni-search-mic', { 'omni-search-mic--recording': recording })}
                data-testid="omni-search-mic"
                icon={faMicrophone}
                size="sm"
                variant="ghost"
                onClick={handleMicClick}
              />
            </InputGroup.Addon>
          )}
          {open && (
            <InputGroup.Addon>
              <Button
                aria-label={query ? 'Clear search' : 'Close search'}
                className="omni-search-clear"
                data-testid="omni-search-clear"
                icon={faXmark}
                size="sm"
                variant="ghost"
                onClick={handleClear}
              />
            </InputGroup.Addon>
          )}
        </InputGroup>
        {open && (
          <div
            className="omni-search-dropdown d-flex flex-column overflow-hidden"
            data-testid="omni-search-dropdown"
          >
            {recordSearchEnabled && (
              <div className="omni-search-tabs">
                <Tabs orientation="horizontal" value={tab} onValueChange={value => setTab(value as OmniSearchTab)}>
                  <TabsList>
                    {TABS.map(({ label, value }) => (
                      <Tab key={value} value={value}>{label}</Tab>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
            )}
            <div className="omni-search-results flex-fill overflow-y-auto">
              {aiEnabled && (
                <>
                  <AskAiRow active={activeIndex === 0} query={query} onSelect={askAssistant} />
                  {/* Nothing follows the row without record search, so the rule would dangle. */}
                  {recordSearchEnabled && <div className="omni-search-ask-ai-divider" />}
                </>
              )}
              {recordSearchEnabled && renderContent()}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
